// Progress view (renamed from "Weekly Call" — SGNL-81) — buckets
// opportunities by what changed between two snapshots. Operators pick
// from/to dates; bucket rows respect the global filter bar (BU / owner
// / stage / state / AI / search) so a Technology-portfolio filter on
// Deal Explorer narrows Progress identically. Backed by
// /api/opportunities/weekly-deltas which accepts ?from=&to=.

import { fetchProgressSummary, fetchWeeklyDeltas } from "../api";
import { esc } from "../lib/format";
import {
  buildExportFilename,
  dateRangeLabel,
  exportTabAsPdf,
} from "../lib/pdf_export";
import { applyFilters, getState } from "../state";
import type { OpportunityRow, WeeklyDeltaRow, WeeklyDeltas } from "../types";

// ---------------------------------------------------------------------------
// SGNL-177 Part 2 — Stage hierarchy mapping
// ---------------------------------------------------------------------------

const STAGE_GROUPS: Record<string, string> = {
  Discovery: "Discovery",
  "Needs Analysis": "Discovery",
  Qualifying: "Discovery",
  "Value Proposition": "Discovery",
  "Id. Decision Makers": "Discovery",
  "Perception Analysis": "Discovery",
  Proposal: "Proposing",
  "Proposal/Price Quote": "Proposing",
  "Proposal Submitted": "Proposing",
  Negotiation: "Negotiating",
  "Negotiation/Review": "Negotiating",
  Contracting: "Negotiating",
  "Verbal Approval": "Negotiating",
  "Closed Won": "Closed",
  "Closed Lost": "Closed",
  "All But Signature": "Negotiating",
};

function stageGroup(stage: string | null | undefined): string {
  if (!stage) return "Other";
  return STAGE_GROUPS[stage] ?? stage;
}

// SGNL-180: single source of truth for the default so init and test-reset stay in sync.
const DEFAULT_STAGE_VIEW_DETAILED = true;
let stageViewDetailed = DEFAULT_STAGE_VIEW_DETAILED;
let _lastSummarisedAt = 0; // SEC-02: 30s cooldown on AI Summary

// ---------------------------------------------------------------------------
// SGNL-210 — Probability filter for Progress sections.
//
// Excludes MQL / Stage-0 / sub-threshold deals from the bucket totals.
// Rep adding a $1M MQL opp at probability=0 should NOT distort the
// revenue projection — that's pre-sales noise, not actual movement.
//
// Default is "Qualified+" (≥5%) per operator feedback. Selection
// persists in sessionStorage so re-opens stay consistent within a
// single browser tab.
// ---------------------------------------------------------------------------

/** Preset bands offered in the dropdown. `min === null` means "no filter".
 *  PR-review typescript-pro: `as const` narrows `value` to the literal
 *  union (`"all" | "q5" | "m30" | "l70"`), giving compile-time safety
 *  at every call site. `setProbabilityPreset` accepts the broader
 *  `string` so the sessionStorage boundary stays runtime-validated. */
export const PROBABILITY_PRESETS = [
  { value: "all", label: "All probabilities", min: null },
  { value: "q5", label: "Qualified+ (≥5%)", min: 5 },
  { value: "m30", label: "Mid-stage+ (≥30%)", min: 30 },
  { value: "l70", label: "Late-stage+ (≥70%)", min: 70 },
] as const;

export type ProbabilityPresetValue = (typeof PROBABILITY_PRESETS)[number]["value"];
export type ProbabilityPreset = (typeof PROBABILITY_PRESETS)[number];

const DEFAULT_PROBABILITY_PRESET: ProbabilityPresetValue = "q5";
const SESSION_KEY = "signal:progress-probability-preset";

// Module-level cache backed by sessionStorage. Read once at module load;
// each setter persists back. Try/catch survives privacy modes where
// sessionStorage throws (Safari Private Browsing pre-iOS 17).
function isValidPreset(v: unknown): v is ProbabilityPresetValue {
  return typeof v === "string" && PROBABILITY_PRESETS.some((p) => p.value === v);
}

function readPersistedPreset(): ProbabilityPresetValue {
  try {
    const v = sessionStorage.getItem(SESSION_KEY);
    if (isValidPreset(v)) return v;
  } catch {
    // sessionStorage unavailable — fall back to default.
  }
  return DEFAULT_PROBABILITY_PRESET;
}

let _probabilityPreset: ProbabilityPresetValue = readPersistedPreset();

export function setProbabilityPreset(value: string): void {
  if (!isValidPreset(value)) return;
  _probabilityPreset = value;
  try {
    sessionStorage.setItem(SESSION_KEY, value);
  } catch {
    // Same fallback as readPersistedPreset — silent.
  }
}

export function getProbabilityPreset(): ProbabilityPresetValue {
  return _probabilityPreset;
}

export function getProbabilityMin(): number | null {
  return PROBABILITY_PRESETS.find((p) => p.value === _probabilityPreset)?.min ?? null;
}

/** Apply the active probability filter across the in-progress Progress
 *  buckets.
 *
 * Returns a fresh WeeklyDeltas with the in-progress buckets filtered
 * to rows whose `probability` is ≥ the active threshold.
 *
 * Architect MEDIUM (pre-PR review): Closed Lost rows pass through
 * UNCHANGED. The user intent is "exclude pre-sales noise"; a deal that
 * actually closed Lost is post-sales — its outcome is real revenue
 * movement regardless of the snapshot probability value. The original
 * implementation treated a NULL closed-lost probability as 0 and swept
 * the entire bucket on the default ≥5% threshold, which made the
 * "Closed Lost" bucket misleadingly empty.
 *
 * Security LOW (pre-PR review): `typeof r.probability === "number"`
 * guard prevents a string-typed BE response (`"50"`) from silently
 * coercing through `??`. Type-drift defence in depth. */
export function applyProbabilityFilter(
  data: WeeklyDeltas,
  min: number | null,
): WeeklyDeltas {
  if (min === null) return data;
  const keep = (rows: WeeklyDeltaRow[]) =>
    rows.filter((r) =>
      typeof r.probability === "number" && Number.isFinite(r.probability)
        ? r.probability >= min
        : false,
    );
  return {
    ...data,
    new_opps: keep(data.new_opps),
    // Closed Lost — pass-through. Confirmed losses are not pre-sales
    // noise, so the probability cap shouldn't sweep them.
    closed_lost: data.closed_lost,
    increase: keep(data.increase),
    drop: keep(data.drop),
  };
}

// SGNL-210 — cache the last-fetched WeeklyDeltas + the filteredOpps
// scope so that a pure UI change (probability preset toggle) re-renders
// the body without paying for a fresh /weekly-deltas round-trip.
// Architect MED + code-review HIGH on the pre-PR review: the prior
// change handler called void renderWeeklyCallTab which re-entered the
// fetch path on every dropdown click — a wasted network call that
// also flashed the "Loading…" placeholder over the tab.
let _cachedDeltas: WeeklyDeltas | null = null;
let _cachedFilteredOpps: OpportunityRow[] | null = null;

/** Test-only: reset module-level state so consecutive test cases don't share cooldown / toggle state. */
export function _resetProgressModuleState(): void {
  stageViewDetailed = DEFAULT_STAGE_VIEW_DETAILED;
  _lastSummarisedAt = 0;
  _probabilityPreset = DEFAULT_PROBABILITY_PRESET;
  _cachedDeltas = null;
  _cachedFilteredOpps = null;
  try {
    // Same sessionStorage guard as readPersistedPreset / setProbabilityPreset
    // — throws in Safari Private Browsing pre-iOS 17.
    sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

export interface ProgressExportInputs {
  filename: string;
  filterChips: string[];
  effectiveFrom: string | null;
  effectiveTo: string;
}

export function buildProgressExportInputs(opts: {
  filters: {
    bu?: string | null;
    owner?: string | null;
    dateRange?: string | null;
    dateFrom?: string | null;
    dateTo?: string | null;
  };
  fromInputValue?: string;
  toInputValue?: string;
  fallbackFrom: string;
  fallbackTo: string;
}): ProgressExportInputs {
  // PRA-3: trim everything user-controllable so "   " doesn't survive
  // the truthy check and produce misleading chips / empty-token filenames.
  const bu = opts.filters.bu?.trim() || null;
  const owner = opts.filters.owner?.trim() || null;
  const { dateRange, dateFrom, dateTo } = opts.filters;
  const dateLabel = dateRangeLabel(dateRange, undefined, dateFrom, dateTo);
  const fromValue = opts.fromInputValue?.trim();
  const toValue = opts.toInputValue?.trim();
  const effectiveFrom = fromValue || opts.fallbackFrom.trim() || null;
  const effectiveTo = toValue || opts.fallbackTo.trim() || "today";
  const filename = buildExportFilename({
    reportName: "Progress",
    scope: dateLabel,
    bu,
    owner,
    fromDate: effectiveFrom,
    toDate: effectiveTo === "today" ? null : effectiveTo,
  });
  const filterChips = [
    bu ? `BU: ${bu}` : null,
    owner ? `Owner: ${owner.split("@")[0].replace(/[^a-zA-Z0-9._-]/g, "_")}` : null,
    `Close date scope: ${dateLabel}`,
    `Comparison: ${effectiveFrom || "?"} → ${effectiveTo}`,
  ].filter(Boolean) as string[];
  return { filename, filterChips, effectiveFrom, effectiveTo };
}

const fmtMoney = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return "—";
  if (Math.abs(n) >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (Math.abs(n) >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
};

const fmtSignedMoney = (n: number | null | undefined): string => {
  if (n === null || n === undefined) return "—";
  const sign = n > 0 ? "+" : "";
  return `${sign}${fmtMoney(n)}`;
};

const fmtDelta = (current: number | null, prev: number | null): string => {
  if (current === null && prev === null) return "—";
  return `${fmtMoney(prev)} → ${fmtMoney(current)}`;
};

const todayISO = (): string => new Date().toISOString().slice(0, 10);
const daysAgoISO = (n: number): string => {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - n);
  return d.toISOString().slice(0, 10);
};

// Persist the user's picked window across re-renders driven by the
// global filter bar. Without this, every filter change would reset the
// pickers to "today−7d → today" — annoying when the operator is
// comparing a specific historical window. State is module-scope rather
// than in state.ts because Progress is the only consumer; a future
// surface that needs the window globally can lift this into state.ts.
let pickedFrom: string = daysAgoISO(7);
let pickedTo: string = todayISO();

function bucketTable(rows: WeeklyDeltaRow[], showDelta: boolean): string {
  if (rows.length === 0) {
    return `<p class="empty-bucket">No opportunities in this bucket.</p>`;
  }
  // SGNL-177 Part 2: stage column shows group or full stage depending on toggle.
  const stageLabel = stageViewDetailed ? "Stage (detailed)" : "Stage";
  return `
    <table class="data-table">
      <thead>
        <tr>
          <th>Account</th>
          <th>Portfolio Lead</th>
          <th>Owner</th>
          <th>Opportunity</th>
          <th>${stageLabel}</th>
          <th>Close date</th>
          <th class="num">CY Rev</th>
          ${showDelta ? '<th class="num">Δ CY Rev</th>' : ""}
        </tr>
      </thead>
      <tbody>
        ${rows
          .map(
            (r) => `
          <tr>
            <td>${esc(r.account_name ?? "—")}</td>
            <td>${esc(r.vp ?? "—")}</td>
            <td>${esc(r.owner_name ?? "—")}</td>
            <td>${esc(r.name)}</td>
            <td>${esc(stageViewDetailed ? (r.stage ?? "—") : stageGroup(r.stage))}</td>
            <td>${esc(r.close_date ?? "—")}</td>
            <td class="num">${
              showDelta ? fmtDelta(r.cy_amount, r.prev_cy_amount) : fmtMoney(r.cy_amount)
            }</td>
            ${showDelta ? `<td class="num">${fmtSignedMoney(r.delta_cy_amount)}</td>` : ""}
          </tr>`,
          )
          .join("")}
      </tbody>
    </table>`;
}

/** SGNL-210 — sum the $ value tied to each bucket row.
 *
 * For Drop / Increase sections we want the magnitude of CHANGE
 * (delta_cy_amount), absolute-valued so the section header shows the
 * size of the movement. For Closed Lost / New Opps we want the current
 * $ amount (cy_amount) — there is no "delta" notion in those buckets.
 *
 * Security MEDIUM (pre-PR review) + PR-Agent #130 imp 4: only sum
 * values that are ALREADY finite numbers. No `Number()` coercion —
 * strings / nulls / Infinity / NaN from a malformed BE payload are
 * dropped outright rather than silently becoming 0 (or worse,
 * Infinity propagating through fmtMoney). Financial totals must not
 * silently absorb schema drift. */
function bucketTotalDollars(rows: WeeklyDeltaRow[], useDelta: boolean): number {
  let sum = 0;
  for (const r of rows) {
    const v = useDelta ? r.delta_cy_amount : r.cy_amount;
    if (typeof v !== "number" || !Number.isFinite(v)) continue;
    sum += Math.abs(v);
  }
  return sum;
}

function bucketSection(
  title: string,
  emoji: string,
  rows: WeeklyDeltaRow[],
  showDelta: boolean,
  testId: string,
  /** PR-review code-review MED — Drop bucket shows a leading "−" so
   *  the at-a-glance read matches the KPI bar's signed treatment
   *  ("−$450K" not "$450K"). Pass an explicit prefix for sections
   *  whose movement is intrinsically negative. */
  signPrefix: "" | "−" = "",
): string {
  // SGNL-210: per-section total $ shown alongside the count so the
  // operator gets the at-a-glance read without expanding.
  const total = bucketTotalDollars(rows, showDelta);
  const totalLabel = rows.length > 0 ? `${signPrefix}${fmtMoney(total)}` : "—";
  return `
    <details class="weekly-bucket" data-testid="${testId}">
      <summary>
        <span class="bucket-emoji">${emoji}</span>
        <span class="bucket-title">${title}</span>
        <span class="bucket-total" data-testid="${testId}-total">${totalLabel}</span>
        <span class="bucket-count">${rows.length}</span>
      </summary>
      <div class="bucket-body">
        ${bucketTable(rows, showDelta)}
      </div>
    </details>`;
}

/** SGNL-210 — probability filter dropdown rendered above the buckets.
 *
 * Default "Qualified+ (≥5%)" excludes MQL / Stage-0 / sub-threshold
 * pre-sales noise from the bucket totals. Selection persists in
 * sessionStorage so re-opening the tab in the same browser tab
 * preserves the operator's choice. */
function probabilityFilterBar(currentValue: string): string {
  return `
    <div class="progress-probability-filter" data-testid="progress-probability-filter">
      <label class="progress-probability-label">
        Min probability
        <!-- aria-label avoids "SF probability" jargon (code-review LOW)
             so a screen-reader user without Salesforce context still
             understands what's being filtered.
             aria-describedby points at the help paragraph below (PR-Agent
             #130 imp 6) so the rationale is exposed in the SR
             accessibility tree alongside the label. -->
        <select data-testid="progress-probability-select"
                aria-label="Filter by minimum deal probability (%)"
                aria-describedby="progress-probability-help">
          ${PROBABILITY_PRESETS.map(
            (p) =>
              `<option value="${esc(p.value)}" ${currentValue === p.value ? "selected" : ""}>${esc(p.label)}</option>`,
          ).join("")}
        </select>
      </label>
      <p id="progress-probability-help" class="muted progress-probability-help">
        Excludes MQL / Stage 0 deals below the threshold so pre-sales
        noise doesn't distort revenue totals.
      </p>
    </div>`;
}

function pickerBar(from: string, to: string): string {
  // Native <input type="date"> — keeps the surface accessible + avoids
  // shipping a date-picker library. Operators are on modern Chrome/Edge
  // (Bounteous SSO requirement) so the native picker is consistent.
  return `
    <div class="progress-picker" data-testid="progress-picker">
      <label class="progress-picker-label">
        From
        <input type="date" id="progress-from" data-testid="progress-from" value="${esc(from)}" />
      </label>
      <label class="progress-picker-label">
        To
        <input type="date" id="progress-to" data-testid="progress-to" value="${esc(to)}" />
      </label>
      <button type="button" class="progress-picker-apply" data-testid="progress-apply">
        Apply
      </button>
    </div>`;
}

function captionLine(data: WeeklyDeltas): string {
  if (!data.baseline_snapshot_at) {
    // SGNL-81 — empty-state inline note replaces the prior warning
    // banner. Friendly nudge tied to the from-date picker so the
    // operator knows what to change rather than what's broken.
    const earliest = data.from_date ?? daysAgoISO(7);
    return `
      <p class="progress-empty-note" data-testid="progress-empty">
        No snapshot captured on or after <b>${esc(earliest)}</b>.
        Pick a later <b>From</b> date or wait for tomorrow's snapshot.
      </p>`;
  }
  const baselineDate = new Date(data.baseline_snapshot_at).toISOString().slice(0, 10);
  return `
    <p class="weekly-baseline" data-testid="weekly-baseline">
      Comparing snapshot on <b>${esc(baselineDate)}</b>
      vs <b>${esc(data.to_date ?? "today")}</b>.
    </p>`;
}

function intersectWithFilters(
  data: WeeklyDeltas,
  filteredOpps: OpportunityRow[] | null,
): WeeklyDeltas {
  // SGNL-81 — global filter integration. If a filtered opp list is
  // supplied, restrict every bucket to opps whose id appears in it.
  // null/undefined means "no filter context" — render everything.
  if (!filteredOpps) return data;
  const allowed = new Set(filteredOpps.map((o) => o.id));
  const keep = (rows: WeeklyDeltaRow[]) => rows.filter((r) => allowed.has(r.id));
  return {
    ...data,
    new_opps: keep(data.new_opps),
    closed_lost: keep(data.closed_lost),
    increase: keep(data.increase),
    drop: keep(data.drop),
  };
}

function totalCount(data: WeeklyDeltas): number {
  return data.new_opps.length + data.closed_lost.length + data.increase.length + data.drop.length;
}

// SGNL-176: Aggregate KPI summary card — total pipeline movement at a glance.
function progressSummaryBar(data: WeeklyDeltas): string {
  // PR-Agent: NaN guard — non-numeric API values (bad type coercion, string
  // from a future schema change) would propagate through sums and render as
  // "NaN" in the KPI bar without this.
  const toNum = (v: unknown): number => {
    const n = typeof v === "number" ? v : Number(v);
    return Number.isFinite(n) ? n : 0;
  };

  // QUAL-01: return null when all delta values are null so the strip renders
  // "—" (data unavailable) rather than "$0" (implies no change).
  const sumOrNull = (rows: WeeklyDeltaRow[]): number | null => {
    const vals = rows.map((r) => r.delta_cy_amount).filter((v) => v != null);
    if (vals.length === 0) return null;
    return vals.reduce((t, v) => t + toNum(v), 0);
  };

  const increaseRaw = sumOrNull(data.increase);
  const dropRaw = sumOrNull(data.drop);
  const totalIncrease = increaseRaw ?? 0;
  const totalDrop = Math.abs(dropRaw ?? 0);
  const net = totalIncrease - totalDrop;
  const netSign = net >= 0 ? "+" : "";
  const netColor = net >= 0 ? "var(--aqua, #2ecc71)" : "var(--coral, #e74c3c)";
  const newPipeline = data.new_opps.reduce((t, r) => t + toNum(r.cy_amount), 0);

  // ARCH-01: always show the active scope so operators know these are scoped
  // totals. Date scope is always shown (not just for non-default) because the
  // Progress tab is sensitive to which year is selected — YTD vs CY makes a
  // large difference in visible changes and caused operator confusion.
  const { bu, owner, dateRange } = getState().filters;
  const today = new Date();
  const y = today.getUTCFullYear();
  const dateScopeMap: Record<string, string> = {
    ytd: `YTD ${y} (Jan–today)`,
    cy0: `${y}`,
    "open-cy0": `Active ${y}+ (open & future)`,
    cy1: `${y - 1}`,
    cy2: `${y - 2}`,
    q1: `Q1 ${y}`,
    r12: "Rolling 12m",
    all: "All dates",
    custom: (getState().filters.dateFrom && getState().filters.dateTo)
      ? `${getState().filters.dateFrom} – ${getState().filters.dateTo}`
      : getState().filters.dateFrom ? `${getState().filters.dateFrom}+` : "Custom",
  };
  const dateLabel = dateScopeMap[dateRange ?? "cy0"] ?? dateRange ?? `${y}`;
  const scopeParts = [bu ? `BU: ${bu}` : null, owner ? `Owner: ${owner}` : null, `Close date: ${dateLabel}`].filter(Boolean) as string[];
  const scopeLabel = `<span class="muted" style="font-size:0.8em; margin-left:auto;">` +
    `Scope: ${esc(scopeParts.join(" · "))}` +
    `</span>`;

  return `
    <div class="kpi-strip kpi-strip--boxed" data-testid="progress-kpi-bar" style="margin-bottom:12px;">
      ${scopeLabel}
      <div class="kpi"><span class="kpi-label">↑ Revenue increase</span><span class="kpi-value" style="color:var(--aqua,#2EEFEC);">${increaseRaw != null ? fmtMoney(totalIncrease) : "—"}</span></div>
      <div class="kpi"><span class="kpi-label">↓ Revenue drop</span><span class="kpi-value" style="color:var(--coral,#FF4759);">${dropRaw != null ? "−" + fmtMoney(totalDrop) : "—"}</span></div>
      <div class="kpi"><span class="kpi-label">Net change</span><span class="kpi-value" style="color:${netColor}; font-weight:700;">${netSign}${fmtMoney(net)}</span></div>
      <div class="kpi"><span class="kpi-label">New pipeline</span><span class="kpi-value">${fmtMoney(newPipeline)} <span class="muted" style="font-size:0.8em;">(${data.new_opps.length} opps)</span></span></div>
      <div class="kpi"><span class="kpi-label">Closed Lost</span><span class="kpi-value">${data.closed_lost.length} opps</span></div>
      <div class="kpi"><span class="kpi-label">Total changes</span><span class="kpi-value">${totalCount(data)}</span></div>
    </div>`;
}

export async function renderWeeklyCallTab(
  host: HTMLElement,
  filteredOpps: OpportunityRow[] | null = null,
  /** SGNL-210: when true, skip the /weekly-deltas fetch and re-render
   *  from the in-memory cache. Used by the probability-preset dropdown
   *  so dropdown toggles don't burn a network round-trip (architect MED
   *  + code-review HIGH on the pre-PR review). The cache lives in
   *  module-level state; on a cache miss we fall through to the fetch
   *  path so the contract is always "render something." */
  useCachedData: boolean = false,
): Promise<void> {
  let data: WeeklyDeltas;
  if (useCachedData && _cachedDeltas) {
    data = _cachedDeltas;
    // PR-Agent #130 imp 6: don't widen scope to unfiltered if the
    // cache is missing the filteredOpps capture. Fall back to the
    // caller-supplied filter (which the change handler passes through
    // explicitly) rather than blindly overwriting with null.
    filteredOpps = _cachedFilteredOpps ?? filteredOpps;
  } else {
    host.innerHTML = `<div class="loading">Loading…</div>`;
    try {
      data = await fetchWeeklyDeltas({ fromDate: pickedFrom, toDate: pickedTo });
    } catch (e) {
      host.innerHTML = `<div class="error">Failed to load: ${esc((e as Error).message)}</div>`;
      return;
    }
    // SGNL-210: cache the raw deltas + the filteredOpps scope so the
    // probability-preset dropdown can re-render in place without
    // re-fetching /weekly-deltas (architect MED + code-review HIGH).
    _cachedDeltas = data;
    _cachedFilteredOpps = filteredOpps;
  }

  // SGNL-210: apply the probability filter AFTER the global filter
  // intersection so the bucket totals + the KPI summary reflect the
  // probability cap. Default "Qualified+ (≥5%)" excludes MQL noise
  // from "actual revenue" calculations.
  const probabilityMin = getProbabilityMin();
  const globallyScoped = intersectWithFilters(data, filteredOpps);
  const scoped = applyProbabilityFilter(globallyScoped, probabilityMin);

  host.innerHTML = `
    <div class="tab-content" data-testid="weekly-call-tab">
      <div class="tab-header" style="justify-content:space-between;">
        <h2>
          Progress
          <!-- aria-live region announces filtered-count changes when
               the operator switches the probability preset (pre-PR
               a11y review). The live region wraps just the count
               span; screen readers re-announce it on text mutation. -->
          <span class="row-count" data-testid="weekly-row-count" aria-live="polite">
            ${totalCount(scoped)} changes
          </span>
        </h2>
        <span style="display:flex;gap:8px;align-items:center;">
          <button class="action-link" data-action="toggle-stage-view" data-testid="stage-view-toggle" type="button">
            <span aria-hidden="true">${stageViewDetailed ? "↑" : "↓"}</span> ${stageViewDetailed ? "High-level stages" : "Detailed stages"}
          </button>
          <button class="action-link" data-action="summarise-progress" data-testid="summarise-btn" type="button" aria-label="Generate AI summary of pipeline changes"><span aria-hidden="true">✦</span> AI Summary</button>
          <button class="action-link" data-action="export-pdf" data-testid="export-pdf-btn" type="button" aria-label="Export progress report as PDF"><span aria-hidden="true">⬇</span> Export PDF</button>
        </span>
      </div>
      <div data-testid="ai-summary-slot" style="margin:0 0 12px;"></div>
      ${pickerBar(pickedFrom, pickedTo)}
      ${probabilityFilterBar(getProbabilityPreset())}
      ${captionLine(data)}
      ${progressSummaryBar(scoped)}
      ${bucketSection("Closed Lost", "❌", scoped.closed_lost, false, "bucket-closed-lost")}
      ${bucketSection("Drop in Revenue", "📉", scoped.drop, true, "bucket-drop", "−")}
      ${bucketSection("Increase in Revenue", "📈", scoped.increase, true, "bucket-increase")}
      ${bucketSection("New Opportunities", "🆕", scoped.new_opps, false, "bucket-new")}
    </div>`;

  // Wire the Apply button to re-fetch with the picked dates. architect M
  // on PR #67: the prior version closed over the `filteredOpps` param
  // passed to this invocation, so if the operator changed the global
  // filter while the picker was open and then clicked Apply, the
  // bucket re-render would use a STALE filtered set. Read fresh from
  // state.ts every click instead — the parent `filteredOpps` arg is
  // only used for the FIRST render driven by main.ts; subsequent
  // re-renders driven by Apply pull current state.
  const applyBtn = host.querySelector<HTMLButtonElement>('[data-testid="progress-apply"]');
  const fromInput = host.querySelector<HTMLInputElement>("#progress-from");
  const toInput = host.querySelector<HTMLInputElement>("#progress-to");
  applyBtn?.addEventListener("click", () => {
    if (fromInput?.value) pickedFrom = fromInput.value;
    if (toInput?.value) pickedTo = toInput.value;
    const state = getState();
    const fresh = state.data ? applyFilters(state.data.opps, state.filters) : null;
    void renderWeeklyCallTab(host, fresh);
  });

  // SGNL-210: probability-filter dropdown. Persists in sessionStorage
  // via setProbabilityPreset and re-renders the buckets in place.
  // useCachedData=true skips the /weekly-deltas refetch — the filter
  // is purely client-side. Focus is restored to the (newly rendered)
  // select so keyboard / screen-reader users keep their place (pre-PR
  // a11y review).
  const probSelect = host.querySelector<HTMLSelectElement>(
    '[data-testid="progress-probability-select"]',
  );
  probSelect?.addEventListener("change", async () => {
    setProbabilityPreset(probSelect.value);
    await renderWeeklyCallTab(host, _cachedFilteredOpps, /* useCachedData */ true);
    host
      .querySelector<HTMLSelectElement>('[data-testid="progress-probability-select"]')
      ?.focus();
  });

  // ── SGNL-177 Part 1: AI Summary button ──────────────────────────────────
  host.querySelector<HTMLButtonElement>('[data-action="summarise-progress"]')
    ?.addEventListener("click", async () => {
      const slot = host.querySelector<HTMLElement>('[data-testid="ai-summary-slot"]');
      if (!slot) return;
      const btn = host.querySelector<HTMLButtonElement>('[data-action="summarise-progress"]');
      // SEC-02: 30s cooldown to prevent cost-bomb on rapid re-clicks.
      const now = Date.now();
      if (now - _lastSummarisedAt < 30_000) {
        slot.innerHTML = `<p class="muted" style="font-size:13px;">Please wait ${Math.ceil((30_000 - (now - _lastSummarisedAt)) / 1000)}s before regenerating.</p>`;
        return;
      }
      if (btn) { btn.disabled = true; btn.textContent = "✦ Generating…"; }
      slot.innerHTML = `<div class="muted" style="font-size:13px;padding:8px 12px;background:var(--bg);border-radius:6px;border:1px solid var(--line);">Generating summary…</div>`;
      try {
        _lastSummarisedAt = Date.now();
        const result = await fetchProgressSummary(
          pickedFrom && pickedTo ? { fromDate: pickedFrom, toDate: pickedTo } : undefined,
        );
        slot.innerHTML = `
          <div style="padding:12px 16px;background:var(--bg);border-radius:8px;border:1px solid var(--line);line-height:1.6;" data-testid="ai-summary-card">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
              <span style="font-size:11px;font-weight:600;text-transform:uppercase;letter-spacing:.08em;color:var(--purple-rain);">✦ AI Summary</span>
            </div>
            <p style="margin:0;font-size:13px;color:var(--ink);" data-testid="ai-summary-text">${esc(result.summary)}</p>
          </div>`;
      } catch {
        slot.innerHTML = `<p class="muted" style="font-size:13px;">Could not generate summary — retry?</p>`;
      } finally {
        if (btn) { btn.disabled = false; btn.textContent = "✦ AI Summary"; }
      }
    });

  // ── SGNL-177 Part 2: Stage hierarchy toggle ─────────────────────────────
  host.querySelector<HTMLButtonElement>('[data-action="toggle-stage-view"]')
    ?.addEventListener("click", () => {
      stageViewDetailed = !stageViewDetailed;
      const state = getState();
      const fresh = state.data ? applyFilters(state.data.opps, state.filters) : null;
      void renderWeeklyCallTab(host, fresh);
    });

  // ── SGNL-177 Part 3: PDF export ─────────────────────────────────────────
  // Builds the export inputs (filter chips, filename, popup-blocked
  // toast handler) and delegates the actual popup-and-print mechanics
  // to lib/pdf_export.ts (factored out for SGNL-179 so the Reports tab
  // can reuse the same flow).
  host.querySelector<HTMLButtonElement>('[data-action="export-pdf"]')
    ?.addEventListener("click", () => {
      // TEST-04 (PR #113): the chip + filename construction is extracted
      // into a pure exported helper (buildProgressExportInputs) so it
      // can be unit-tested in isolation from the DOM click handler.
      const inputs = buildProgressExportInputs({
        filters: getState().filters,
        fromInputValue: host.querySelector<HTMLInputElement>("#progress-from")?.value,
        toInputValue: host.querySelector<HTMLInputElement>("#progress-to")?.value,
        fallbackFrom: pickedFrom,
        fallbackTo: pickedTo,
      });
      const { filename, filterChips } = inputs;

      const tabEl = host.querySelector<HTMLElement>('[data-testid="weekly-call-tab"]');
      if (!tabEl) return;

      exportTabAsPdf({
        tabEl,
        filterChips,
        filename,
        title: "Progress Report",
        // ARCH-06 (PR #113): the picker selector is Progress-tab-specific;
        // the generic helper shouldn't know about it. Pass it from the
        // call site instead.
        stripSelectors: ["[data-testid='progress-picker']"],
        onPopupBlocked: () => {
          // Inline toast in the AI Summary slot (SEC-03 from SGNL-177:
          // never use alert(), keep the affordance in-page).
          const slot = host.querySelector<HTMLElement>(
            '[data-testid="ai-summary-slot"]',
          );
          if (!slot) return;
          slot.innerHTML = `<p class="muted" style="font-size:13px;padding:6px 0;">Popup blocked — allow pop-ups for this site, or use <kbd>⌘+P</kbd> / <kbd>Ctrl+P</kbd> to print directly.</p>`;
          window.setTimeout(() => {
            if (slot.querySelector("p")) slot.innerHTML = "";
          }, 6000);
        },
      });
    });
}
